import React from "react";
import { useParams } from "react-router-dom";
import { BedDoubleIcon, BathIcon, CarIcon, LayersIcon, FileTextIcon } from "lucide-react";
import { Breadcrumb } from "../components/shared/Breadcrumb";
import { ButtonLink } from "../components/shared/Button";
import { DesignCard } from "../components/shared/DesignCard";
import { FaqAccordion } from "../components/shared/FaqAccordion";
import { IndicativePricing } from "../components/shared/IndicativePricing";
import { PageMeta } from "../components/shared/PageMeta";
import { NotFound } from "./NotFound";
import { designs } from "../data/designs";

// Design-detail template → Bricks single template for the "design" CPT. No Product/Offer schema.
const designFaqs = [{
  question: "Can this design be changed?",
  answer: "Talk with us about the changes you have in mind. Whether they’re possible depends on the design, your block and planning requirements, and changes can affect cost."
}, {
  question: "Will it fit my block?",
  answer: "The frontage category is a starting point only. We review width, depth, orientation, setbacks, slope, easements and local planning rules with you."
}, {
  question: "What’s the difference between living area and total under roof?",
  answer: "Living area covers the internal living spaces. Total under roof is larger because it typically also includes roofed areas such as the garage and covered outdoor spaces. Ask the team for the exact breakdown."
}];
export function DesignDetail() {
  const {
    slug
  } = useParams();
  const design = designs.find(item => item.slug === slug);
  if (!design) return <NotFound data-id="element-625" data-mp="fszusz6wlbfh" />;
  const related = designs.filter(item => item.slug !== design.slug);
  const livingShare = Math.round(design.livingArea / design.totalUnderRoof * 100);
  const specs = [{
    label: "Bedrooms",
    value: String(design.bedrooms),
    icon: BedDoubleIcon
  }, {
    label: "Bathrooms",
    value: String(design.bathrooms),
    icon: BathIcon
  }, {
    label: "Car spaces",
    value: String(design.carSpaces),
    icon: CarIcon
  }, {
    label: "Storeys",
    value: "Single",
    icon: LayersIcon
  }];
  return <>
      <PageMeta title={`${design.name} | ${design.frontage} Frontage Home Design | Oceanside Living`} description={`${design.name}: a single-storey, ${design.bedrooms}-bedroom design for the ${design.frontage} frontage category. ${design.livingArea} m² living, ${design.totalUnderRoof} m² total under roof.`} data-id="element-626" data-mp="3sddizeta6mek" />
      <div className="mx-auto max-w-site px-5 pt-10 md:px-10 md:pt-14" data-id="element-627" data-mp="ewwfcpdude8k">
        <Breadcrumb items={[{
        label: "Home",
        to: "/"
      }, {
        label: "Home designs",
        to: "/home-designs"
      }, {
        label: design.name
      }]} data-id="element-628" data-mp="ew4ht90op6fn" />

        <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-629" data-mp="56urnypehxu6">
          <div className="lg:col-span-6" data-id="element-630" data-mp="5504seukjvng">
            <p className="text-[15px] text-slate" data-id="element-631" data-mp="sw5j2pkv2fvn">Single-storey · {design.frontage} frontage category</p>
            <h1 className="mt-2 text-[40px] font-semibold leading-[1.05] tracking-tight md:text-[64px]" data-id="element-632" data-mp="xnnlzj6g2qj9">{design.name}</h1>
            <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-slate md:text-lg" data-id="element-633" data-mp="2ichvtx1ernn2">{design.description}</p>
            <p className="mt-4 inline-flex rounded-full bg-paper-deep px-4 py-2 text-[15px] font-medium text-charcoal" data-id="element-634" data-mp="2deg2mja9q7xv">
              Lifestyle fit: {design.lifestyleFit}
            </p>
            <dl className="mt-8 grid grid-cols-2 border-t border-charcoal sm:grid-cols-4" data-id="element-635" data-mp="2n5ubvis0um47">
              {specs.map(spec => <div key={spec.label} className="border-b border-line py-4 sm:border-b-0" data-id="element-636" data-mp="1zgrtu9th6r96">
                  <spec.icon className="h-5 w-5 text-slate" aria-hidden="true" data-id="element-637" data-mp="1uhyfygaa0y8h" />
                  <dt className="mt-2 text-sm text-slate" data-id="element-638" data-mp="29epeyog1g7xk">{spec.label}</dt>
                  <dd className="font-heading text-2xl font-semibold" data-id="element-639" data-mp="24d03x8stfu9h">{spec.value}</dd>
                </div>)}
            </dl>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-640" data-mp="3kue51ool9n2l">
              <ButtonLink to={`/contact?design=${design.slug}`} data-id="element-641" data-mp="3fseyy71gsc1y">Discuss this design</ButtonLink>
              <ButtonLink to={`/contact?design=${design.slug}&request=floor-plan`} variant="secondary" data-id="element-642" data-mp="1pd4jvn3uhbss">Request this floor plan</ButtonLink>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-6" data-id="element-643" data-mp="1yc9l516sjj8n">
            <figure className="overflow-hidden rounded-[28px] bg-paper-deep" data-id="element-644" data-mp="1ybgb9rcjcdma">
              <img src={design.image} alt={`${design.name} façade render`} width={698} height={468} className="aspect-[3/2] w-full object-cover" data-id="element-645" data-mp="23cnh3kv4b2qb" />
              <figcaption className="border-t border-line px-6 py-4 text-sm text-slate" data-id="element-646" data-mp="2da1z3owovzkq">
                {design.name} façade render, {design.frontage} frontage. Façades and finishes may vary. Ask us for the current floor plan.
              </figcaption>
            </figure>
            <div className="rounded-[28px] border border-line bg-white p-7" data-id="element-647" data-mp="2m1kgu49aanfx">
              <h2 className="text-[20px] font-semibold" data-id="element-648" data-mp="2r2rom3bffmml">Key features</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-649" data-mp="2vzqeuaryz9fi">
                {design.features.map(feature => <li key={feature} className="flex items-start gap-3 text-[16px]" data-id="element-650" data-mp="3659ih37h6cv0">
                    <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-ocean" data-id="element-651" data-mp="3153wa2jz5ikb" />
                    {feature}
                  </li>)}
              </ul>
              <p className="mt-5 border-t border-line pt-4 text-[15px] text-slate" data-id="element-652" data-mp="2s5777ztoahoq">
                {design.totalUnderRoof}m² total under roof. {design.outdoorNote}.
              </p>
            </div>
          </div>
        </div>
      </div>

      <section aria-labelledby="area-heading" className="mx-auto mt-16 max-w-site px-5 md:mt-24 md:px-10" data-id="element-653" data-mp="3jwn0gcpqj2nx">
        <div className="grid gap-10 border-t border-line pt-12 lg:grid-cols-12" data-id="element-654" data-mp="3f3bnruy8rro3">
          <div className="lg:col-span-4" data-id="element-655" data-mp="3b31kwr5qxhdg">
            <h2 id="area-heading" className="text-[28px] font-semibold tracking-tight md:text-[36px]" data-id="element-656" data-mp="bg1nkktzfpfj">Floor area, clearly labelled</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate" data-id="element-657" data-mp="6oa5kyow6s10">Living area and total under roof measure different things. Check both when comparing designs.</p>
          </div>
          <div className="space-y-6 lg:col-span-8" data-id="element-658" data-mp="25wtakazilokp">
            <div data-id="element-659" data-mp="25ycse60tk7ol">
              <div className="flex items-baseline justify-between" data-id="element-660" data-mp="1r7dmx3va5z98"><span className="font-semibold" data-id="element-661" data-mp="1r6m1glvedhwo">Living area</span><span className="font-heading text-2xl font-semibold" data-id="element-662" data-mp="1w6gtwtutmfju">{design.livingArea} m²</span></div>
              <div className="mt-2 h-3 bg-paper-deep" data-id="element-663" data-mp="1i6p9aa9su0wv"><div className="h-3 bg-ocean" style={{
                width: `${livingShare}%`
              }} data-id="element-664" data-mp="1i5nubupnwu9v" /></div>
            </div>
            <div data-id="element-665" data-mp="1n6jxg626wce8">
              <div className="flex items-baseline justify-between" data-id="element-666" data-mp="1n73jnyyvhmy2"><span className="font-semibold" data-id="element-667" data-mp="1891an992o99f">Total under roof</span><span className="font-heading text-2xl font-semibold" data-id="element-668" data-mp="18am8b7qoo8bi">{design.totalUnderRoof} m²</span></div>
              <div className="mt-2 h-3 bg-charcoal" data-id="element-669" data-mp="1d9fml2dso3cq" />
            </div>
            <p className="text-[15px] text-slate" data-id="element-670" data-mp="32n2b1yk9t4bm">Specifications are from Oceanside’s published design information and may change with plan revisions.</p>
          </div>
        </div>
      </section>

      <section aria-labelledby="plan-heading" className="mx-auto mt-16 max-w-site px-5 md:mt-24 md:px-10" data-id="element-671" data-mp="vytk6e7r78g2">
        <div className="grid gap-8 border border-line bg-white p-7 md:p-10 lg:grid-cols-12" data-id="element-672" data-mp="r10fxqq0goif">
          <div className="lg:col-span-8" data-id="element-673" data-mp="15po5ngd6e40g">
            <FileTextIcon className="h-7 w-7 text-slate" aria-hidden="true" data-id="element-674" data-mp="10xd5cceiwg7d" />
            <h2 id="plan-heading" className="mt-4 text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-675" data-mp="10vab1g6c80wu">Floor plan</h2>
            <p className="mt-3 max-w-2xl text-[17px] leading-relaxed text-slate" data-id="element-676" data-mp="1angafayx5km2">
              The current approved floor plan for {design.name} isn’t shown online yet. Request it and the team will send the
              latest version and talk you through it.
            </p>
          </div>
          <div className="flex items-end lg:col-span-4 lg:justify-end" data-id="element-677" data-mp="3qeawpubus3hg">
            <ButtonLink to={`/contact?design=${design.slug}&request=floor-plan`} variant="secondary" data-id="element-678" data-mp="3qekkdb9fdyjz">Request this floor plan</ButtonLink>
          </div>
        </div>
      </section>

      <section aria-labelledby="fit-heading" className="mx-auto mt-16 max-w-site px-5 md:mt-24 md:px-10" data-id="element-679" data-mp="2zliv6zacub3x">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-680" data-mp="34jk9l6wu5m92">
          <div data-id="element-681" data-mp="39bbv3314x1sc">
            <h2 id="fit-heading" className="text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-682" data-mp="39cwnon3w2ank">Will it suit your block?</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate" data-id="element-683" data-mp="3ea6m2s999wnj">
              The {design.frontage} frontage category is a starting point, not a guarantee. Depth, orientation, setbacks, slope,
              easements and local planning rules all affect whether {design.name} suits your site.
            </p>
          </div>
          <IndicativePricing designName={design.name} data-id="element-684" data-mp="2vkguee0shqzu" />
        </div>
      </section>

      <section aria-labelledby="related-heading" className="mx-auto mt-16 max-w-site px-5 md:mt-24 md:px-10" data-id="element-685" data-mp="2vi41x6xeny8j">
        <h2 id="related-heading" className="text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-686" data-mp="au7wui2m3cs2">Other designs</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-687" data-mp="1vcs6s0gz7e0i">
          {related.map(item => <DesignCard key={item.slug} design={item} data-id="element-688" data-mp="1qcnny3sli774" />)}
        </div>
      </section>

      <section aria-labelledby="design-faq-heading" className="mx-auto mt-16 max-w-site px-5 md:mt-24 md:px-10" data-id="element-689" data-mp="1lmh1oalk2ys1">
        <h2 id="design-faq-heading" className="mb-6 text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-690" data-mp="1gnw1gb6b0f3l">Questions about {design.name}</h2>
        <FaqAccordion items={designFaqs} data-id="element-691" data-mp="1bpugtlt8c7q0" />
      </section>

      <section className="mt-16 bg-charcoal py-16 text-paper md:mt-24 md:py-20" data-id="element-692" data-mp="17m69934llc4r">
        <div className="mx-auto flex max-w-site flex-col gap-8 px-5 md:flex-row md:items-center md:justify-between md:px-10" data-id="element-693" data-mp="12nl5wuu1ucc3">
          <h2 className="max-w-xl text-[28px] font-semibold leading-tight tracking-tight md:text-[38px]" data-id="element-694" data-mp="xpbfexj9rzwt">Thinking about {design.name}? Let’s talk about your block.</h2>
          <ButtonLink to={`/contact?design=${design.slug}`} data-id="element-695" data-mp="2s2jbesizehk1">Discuss this design</ButtonLink>
        </div>
      </section>
    </>;
}